import React, { useEffect, useState } from "react";
import { shadows } from "@karda/design-tokens";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Grid,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  CalendarToday,
  Description,
  Person,
  RecordVoiceOver,
  TextFields,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  LEGACY_TRANSCRIBED_BY,
  TranscriptListItem,
} from "../../../shared/interfaces/Transcript";
import { getTranscriptThumbnail } from "../../../shared/api/transcriptsApi";
import { useUserDirectory } from "@/features/users/api/userDirectory";
import { useOrgId } from "../../../shared/auth/orgContext";

const formatRecordedDateTime = (ms: number): string => {
  if (!ms) return "";
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return "";
  const formattedDate = date.toLocaleDateString(undefined);
  const formattedTime = date
    .toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    })
    .toLowerCase();
  return `${formattedDate} at ${formattedTime}`;
};

interface TranscriptCardProps {
  transcript: TranscriptListItem;
}

export const TranscriptCard: React.FC<TranscriptCardProps> = ({
  transcript,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isLegacy = transcript.transcribedBy === LEGACY_TRANSCRIBED_BY;
  const isEdited = transcript.versionNumber > 1;

  const organizationId = useOrgId();
  const { resolveName } = useUserDirectory(organizationId ?? undefined);
  const transcribedByName =
    resolveName(transcript.transcribedBy) ?? transcript.transcribedBy;

  const [thumbnailUrl, setThumbnailUrl] = useState<string>("");

  useEffect(() => {
    const controller = new AbortController();
    getTranscriptThumbnail(transcript.evidenceId, controller.signal)
      .then((url) => {
        if (!controller.signal.aborted) setThumbnailUrl(url);
      })
      .catch(() => {
        // No thumbnail available — fall back to the icon placeholder.
      });
    return () => controller.abort();
  }, [transcript.evidenceId]);

  const handleClick = () => {
    navigate(`/transcripts/${transcript.evidenceId}`);
  };

  return (
    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }}>
      <Card
        role="button"
        tabIndex={0}
        aria-label={`Open transcript ${transcript.fileName}`}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        }}
        sx={{
          height: "100%",
          width: "100%",
          minWidth: { xs: "200px", xxl: "240px", uhd: "280px" },
          maxWidth: { xs: "300px", xxl: "360px", uhd: "420px" },
          margin: "auto",
          borderRadius: 2,
          backgroundColor: "background.paper",
          transition: "all 0.3s ease",
          border: 1,
          borderColor: "divider",
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: shadows.raised,
            borderColor: "primary.main",
          },
          "&:focus-visible": {
            transform: "translateY(-4px)",
            boxShadow: shadows.raised,
            borderColor: "primary.main",
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
        }}
      >
        <Box
          sx={{
            height: { xs: 100, xxl: 120, uhd: 140 },
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              theme.palette.mode === "dark" ? 0.25 : 0.12,
            )} 0%, ${alpha(
              theme.palette.primary.main,
              theme.palette.mode === "dark" ? 0.1 : 0.04,
            )} 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            overflow: "hidden",
          }}
        >
          {thumbnailUrl ? (
            <CardMedia
              component="img"
              image={thumbnailUrl}
              alt={transcript.fileName}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          ) : (
            <RecordVoiceOver
              sx={{
                fontSize: 44,
                color: alpha(theme.palette.primary.main, 0.7),
              }}
            />
          )}
          {isEdited && (
            <Chip
              label={`v${transcript.versionNumber}`}
              size="small"
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                fontWeight: "bold",
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                boxShadow: 1,
              }}
            />
          )}
        </Box>

        <CardContent sx={{ p: 2, flexGrow: 1 }}>
          <Stack spacing={1.5}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Description
                fontSize="small"
                sx={{ color: "text.secondary", fontSize: "1rem" }}
              />
              <Tooltip title={transcript.fileName} placement="top">
                <Typography
                  variant="body2"
                  color="text.primary"
                  fontWeight="bold"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {transcript.fileName}
                </Typography>
              </Tooltip>
            </Box>

            <Box display="flex" alignItems="center" gap={1.5}>
              <CalendarToday
                fontSize="small"
                sx={{ color: "text.secondary", fontSize: "1rem" }}
              />
              <Typography variant="body2" color="text.primary">
                {formatRecordedDateTime(transcript.recordedAt)}
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={1.5}>
              <TextFields
                fontSize="small"
                sx={{ color: "text.secondary", fontSize: "1rem" }}
              />
              <Typography variant="body2" color="text.primary">
                {transcript.wordCount.toLocaleString()} words
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={1.5}>
              <Person
                fontSize="small"
                sx={{ color: "text.secondary", fontSize: "1rem" }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {isLegacy ? "Legacy import" : transcribedByName}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );
};
